function getTooltipPosition(
  targetEl: HTMLElement,
  panel: HTMLElement,
  toolbarHostRef: React.RefObject<HTMLElement | null>,
) {
  const rect = targetEl.getBoundingClientRect();
  const hostRect = toolbarHostRef.current?.getBoundingClientRect() ?? panel.getBoundingClientRect();

  return {
    left: rect.left - hostRect.left + rect.width / 2,
    top: rect.bottom - hostRect.top,
  };
};

function showTooltip(
  el: HTMLElement,
  label: string,
  panel: HTMLElement,
  toolbarHostRef: React.RefObject<HTMLElement | null>,
  setPanelTooltip: React.Dispatch<any>,
) {
  const { left, top } = getTooltipPosition(el, panel, toolbarHostRef);

  setPanelTooltip({
    open: true,
    label,
    left,
    top,
    placement: 'bottom',
  });
};

export function attachTooltip(
  panel: HTMLElement,
  toolbarHostRef: React.RefObject<HTMLElement | null>,
  setPanelTooltip: React.Dispatch<any>,
) {
  const hide = () => {
    setPanelTooltip((s) => ({ ...s, open: false }));
  };

  const attach = (el: Element, label: string) => {
    const html = el as HTMLElement;
    if (!html || html.dataset?.airbusFilterTooltipAttached) return;

    html.dataset.airbusFilterTooltipAttached = '1';

    const enter = () => showTooltip(html, label, panel, toolbarHostRef, setPanelTooltip);

    html.addEventListener('mouseenter', enter);
    html.addEventListener('focus', enter);
    html.addEventListener('mouseleave', hide);
    html.addEventListener('blur', hide);
  };

  panel.querySelectorAll('.MuiDataGrid-filterFormDeleteIcon').forEach((el) => attach(el, 'Remove'));

  panel
    .querySelectorAll(
      '.MuiChip-deleteIcon, .MuiAutocomplete-clearIndicator, .MuiInputAdornment-root .MuiIconButton-root',
    )
    .forEach((el) => attach(el, 'Clear'));
};

function isOverlapping(rect1: DOMRect, rect2: DOMRect) {
  return (
    rect1.left < rect2.right &&
    rect1.right > rect2.left &&
    rect1.top < rect2.bottom &&
    rect1.bottom > rect2.top
  );
}

let headerPrevZ = '';
let footerPrevZ = '';

function panelOverlapsHeaderFooter(panelRect: DOMRect, header: HTMLElement, footer: HTMLElement) {
  if (header && isOverlapping(panelRect, header.getBoundingClientRect())) return true;
  if (footer && isOverlapping(panelRect, footer.getBoundingClientRect())) return true;
  return false;
}

function lowerHeaderFooterZ(header: HTMLElement, footer: HTMLElement) {
  if (header) {
    headerPrevZ = header.style.zIndex || '';
    header.style.setProperty('z-index', '1', 'important');
  }
  if (footer) {
    footerPrevZ = footer.style.zIndex || '';

    const newZIndex = '1';
    footer.style.zIndex = newZIndex;
  }
}

export function restoreHeaderFooterZ(header: HTMLElement, footer: HTMLElement) {
  if (header) {
    if (headerPrevZ) {
      header.style.setProperty('z-index', headerPrevZ, 'important');
    } else {
      header.style.removeProperty('z-index');
    }
  }

  if (footer) {
    if (footerPrevZ) {
      footer.style.zIndex = footerPrevZ;
    } else {
      footer.style.removeProperty('z-index');
    }
  }
}

export function replaceFilterDeleteIcons(
  panel: HTMLElement,
  isMobile: boolean,
  deleteOutlineSvg: string,
  deleteFilledSvg: string,
) {
  if (!panel) return;

  const svgs = panel.querySelectorAll(
    '.MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon svg, ' +
      '.MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root',
  );

  svgs.forEach((svg) => {
    if (!(svg instanceof Element)) return;
    if (svg.classList.contains('custom-delete-outline')) return;

    const temp = document.createElement('span');
    temp.innerHTML = isMobile ? deleteOutlineSvg : deleteFilledSvg;

    const newSvg = temp.firstElementChild as SVGElement | null;

    if (newSvg && svg.parentNode) {
      newSvg.classList.add('custom-delete-outline');
      svg.parentNode.replaceChild(newSvg, svg);
    }
  });
};

export function handleFilterDelete(e: MouseEvent | TouchEvent, panel: HTMLElement, apiRef: any)  {
  let el: Element | null = null;

  if (e instanceof MouseEvent) {
    el = e.target instanceof Element ? e.target : null;
  } else {
    const t = e.changedTouches?.[0];
    el = t ? document.elementFromPoint(t.clientX, t.clientY) : null;
  }

  if (!el) return;

  const deleteBtn = el.closest(
    '.MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon, ' +
      'button[aria-label="Delete"], ' +
      '.MuiDataGrid-filterFormDeleteIcon',
  );

  if (!deleteBtn) return;

  const forms = panel.querySelectorAll('.MuiDataGrid-filterForm');

  if (forms.length === 1) {
    panel.dataset.closeReason = 'delete';

    try {
      apiRef.current?.hideFilterPanel?.();
    } catch {}

    if (panel.parentNode === document.body) {
      panel.remove();
    }
  } else {
    console.error(
      '[CustomGridFilterPanel] More than one filter present. Removing clicked filter only.',
    );
  }
};

type DragStateRef = {
  current: {
    isDragging: boolean;
    dragStart: {
      x: number;
      y: number;
      left: number;
      top: number;
    };
  };
};

export function startDrag(
  clientX: number,
  clientY: number,
  panel: HTMLElement,
  dragState: DragStateRef,
) {
  const rect = panel.getBoundingClientRect();

  dragState.current = {
    isDragging: true,
    dragStart: {
      x: clientX,
      y: clientY,
      left: rect.left,
      top: rect.top,
    },
  };

  document.body.style.userSelect = 'none';
};

export function moveDrag(
  clientX: number,
  clientY: number,
  panel: HTMLElement,
  dragState: DragStateRef,
  header?: HTMLElement,
  footer?: HTMLElement,
)  {
  if (!dragState.current.isDragging) return;

  const dx = clientX - dragState.current.dragStart.x;
  const dy = clientY - dragState.current.dragStart.y;

  let newLeft = dragState.current.dragStart.left + dx;
  let newTop = dragState.current.dragStart.top + dy;

  const rect = panel.getBoundingClientRect();
  const maxLeft = window.innerWidth - rect.width;
  const maxTop = window.innerHeight - rect.height;

  newLeft = Math.max(0, Math.min(newLeft, maxLeft));
  newTop = Math.max(0, Math.min(newTop, maxTop));

  panel.style.left = `${newLeft}px`;
  panel.style.top = `${newTop}px`;
  panel.style.transform = '';

  const panelRect = panel.getBoundingClientRect();

  if (panelOverlapsHeaderFooter(panelRect, header, footer)) {
    lowerHeaderFooterZ(header, footer);
  } else {
    restoreHeaderFooterZ(header, footer);
  }
};
